/**
 * ShareExplorationModal — share a knowledge exploration (read-only) with another
 * user by email, and revoke existing shares. Mirror of ShareOntologyModal for
 * explorations (Feedback A13: generalise sharing to knowledge graphs).
 */
import { useState, useEffect, useCallback } from 'react';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  TextInput,
  InlineNotification,
  Button,
  Loading,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListRow,
  StructuredListCell,
  StructuredListBody,
  Tag,
} from '@carbon/react';
import { TrashCan, Share } from '@carbon/icons-react';
import { explorationShareApi, type ExplorationShareInfo } from '@/lib/api/exploration-share.api';

interface ShareExplorationModalProps {
  open: boolean;
  onClose: () => void;
  explorationId: string;
  explorationName: string;
}

export function ShareExplorationModal({
  open,
  onClose,
  explorationId,
  explorationName,
}: ShareExplorationModalProps) {
  const [email, setEmail] = useState('');
  const [shares, setShares] = useState<ExplorationShareInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadShares = useCallback(async () => {
    if (!explorationId) return;
    setSharesLoading(true);
    try {
      setShares(await explorationShareApi.getShares(explorationId));
    } catch (err) {
      console.error('Failed to load exploration shares', err);
    } finally {
      setSharesLoading(false);
    }
  }, [explorationId]);

  useEffect(() => {
    if (open) {
      loadShares();
      setEmail('');
      setError(null);
      setSuccess(null);
    }
  }, [open, loadShares]);

  const handleShare = async () => {
    if (!email.trim()) {
      setError('Please enter an email address.');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await explorationShareApi.shareExploration(explorationId, email.trim());
      setSuccess(`Knowledge graph shared with ${email.trim()} (read-only).`);
      setEmail('');
      await loadShares();
    } catch (err: any) {
      setError(err?.message || 'Unable to share. Please verify the email address.');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (shareId: string, shareEmail: string) => {
    try {
      await explorationShareApi.revokeShare(explorationId, shareId);
      setShares((prev) => prev.filter((s) => s.id !== shareId));
      setSuccess(`Access revoked for ${shareEmail}`);
    } catch (err: any) {
      setError(err?.message || 'Failed to revoke access.');
    }
  };

  return (
    <ComposedModal open={open} onClose={onClose} size="sm">
      <ModalHeader title={`Share "${explorationName}"`} label="Read-only access" />
      <ModalBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
            <TextInput
              id="share-exploration-email-input"
              labelText="User email"
              placeholder="Enter the user's email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
                setSuccess(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !loading) {
                  e.preventDefault();
                  handleShare();
                }
              }}
              disabled={loading}
              style={{ flex: 1 }}
            />
            <Button kind="primary" size="md" renderIcon={Share} onClick={handleShare} disabled={loading || !email.trim()}>
              {loading ? 'Sharing...' : 'Share'}
            </Button>
          </div>

          {error && (
            <InlineNotification kind="error" title="Error" subtitle={error} lowContrast onCloseButtonClick={() => setError(null)} />
          )}
          {success && (
            <InlineNotification kind="success" title="Success" subtitle={success} lowContrast onCloseButtonClick={() => setSuccess(null)} />
          )}

          <div style={{ marginTop: '1rem' }}>
            <h5 style={{ marginBottom: '0.5rem' }}>Currently shared with</h5>
            {sharesLoading ? (
              <Loading small withOverlay={false} description="Loading shares..." />
            ) : shares.length === 0 ? (
              <p style={{ color: 'var(--cds-text-secondary)', fontSize: '0.875rem' }}>
                This knowledge graph is not shared with anyone yet.
              </p>
            ) : (
              <StructuredListWrapper isCondensed>
                <StructuredListHead>
                  <StructuredListRow head>
                    <StructuredListCell head>User</StructuredListCell>
                    <StructuredListCell head>Permission</StructuredListCell>
                    <StructuredListCell head>Actions</StructuredListCell>
                  </StructuredListRow>
                </StructuredListHead>
                <StructuredListBody>
                  {shares.map((share) => (
                    <StructuredListRow key={share.id}>
                      <StructuredListCell>
                        <span>{share.shared_with_display_name || share.shared_with_email}</span>
                        {share.shared_with_display_name && (
                          <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>
                            {share.shared_with_email}
                          </span>
                        )}
                      </StructuredListCell>
                      <StructuredListCell>
                        <Tag type="blue" size="sm">{share.permission}</Tag>
                      </StructuredListCell>
                      <StructuredListCell>
                        <Button
                          kind="danger--ghost"
                          size="sm"
                          hasIconOnly
                          renderIcon={TrashCan}
                          iconDescription="Revoke access"
                          onClick={() => handleRevoke(share.id, share.shared_with_email)}
                        />
                      </StructuredListCell>
                    </StructuredListRow>
                  ))}
                </StructuredListBody>
              </StructuredListWrapper>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>Close</Button>
      </ModalFooter>
    </ComposedModal>
  );
}

// Made with Bob
